export const LEGACY_REQUIREMENT_DEPENDENCY_KEY = "requirementDependency";
export const REQUIRED_AFTER_FIELD_KEY = "requiredWhenFilled";
export const NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY = "notRequiredWhenFilled";
export const REQUIRE_PESEL_FIELD_TYPE = "requirePesel";

const normalizeArrayValue = (value) => (Array.isArray(value) ? value : []);

/**
 * Determines whether a field must always be required, regardless of user input.
 *
 * Currently only the "requirePesel" field falls into this category: it is a
 * special, non-editable field that is mandatory by design. Keeping this rule in
 * a single helper ensures the UI (which only reads the flag) and the payload
 * builder stay in sync.
 */
export const isFieldAlwaysRequired = (field = {}) =>
  field.type === REQUIRE_PESEL_FIELD_TYPE;

export const normalizeFieldDependencies = (field = {}) => {
  const requiredAfterFieldId =
    field[REQUIRED_AFTER_FIELD_KEY] ||
    field[LEGACY_REQUIREMENT_DEPENDENCY_KEY] ||
    "";

  return {
    ...field,
    ...(isFieldAlwaysRequired(field) ? { required: true } : {}),
    [REQUIRED_AFTER_FIELD_KEY]: requiredAfterFieldId,
    [NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY]: normalizeArrayValue(
      field[NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY]
    ),
  };
};

export const hasActiveDependencies = (field = {}) => {
  const normalizedField = normalizeFieldDependencies(field);
  return Boolean(
    normalizedField[REQUIRED_AFTER_FIELD_KEY] ||
      normalizedField[NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY].length
  );
};

export const sanitizeSectionDependencies = (fields = []) => {
  const normalizedFields = normalizeArrayValue(fields).map(
    normalizeFieldDependencies
  );
  const availableFieldIds = new Set(
    normalizedFields
      .filter(
        ({ id, type }) => Boolean(id) && type !== REQUIRE_PESEL_FIELD_TYPE
      )
      .map(({ id }) => id)
  );

  return normalizedFields.map((field) => {
    const {
      id,
      type,
      [REQUIRED_AFTER_FIELD_KEY]: requiredAfterCandidate,
      [NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY]: notRequiredCandidates,
      [LEGACY_REQUIREMENT_DEPENDENCY_KEY]: _legacyRequirementDependency,
      ...restField
    } = field;

    const isInvalidDependency = (candidate) =>
      !candidate || candidate === id || !availableFieldIds.has(candidate);

    const requiredAfterFieldId =
      type === REQUIRE_PESEL_FIELD_TYPE ||
      isInvalidDependency(requiredAfterCandidate)
        ? ""
        : requiredAfterCandidate;

    const notRequiredWhenFilledFieldIds =
      type === REQUIRE_PESEL_FIELD_TYPE
        ? []
        : [...new Set(normalizeArrayValue(notRequiredCandidates))].filter(
            (candidate) => !isInvalidDependency(candidate)
          );

    return {
      ...restField,
      id,
      type,
      [REQUIRED_AFTER_FIELD_KEY]: requiredAfterFieldId,
      [NOT_REQUIRED_WHEN_FILLED_FIELD_IDS_KEY]: notRequiredWhenFilledFieldIds,
    };
  });
};
